import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Sparkles, Send, Bot, User, RefreshCw, Save, CheckCircle2,
  ArrowRight, Undo2, ExternalLink, Link2, Eye, Laptop, Smartphone,
  AlertCircle, HelpCircle, BookOpen, Layers, Check, ChevronRight, Trash2, FolderKanban,
  Paperclip, Image as ImageIcon, X, FileText, UploadCloud, FolderOpen, Plus, Clock, Edit3,
  Search, Grid, ArrowLeft, LayoutTemplate, AlertTriangle
} from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import DynamicModule from '../components/DynamicModule'
import ModuleContentEditorModal from '../components/studio/ModuleContentEditorModal'
import {
  generateOrRefineModule,
  saveDraft,
  loadDraft,
  loadModuleForStudio,
  listDrafts,
  publishCustomModule,
  deleteDraft,
  extractDocLinks,
  loadPublishedCustomModules,
} from '../lib/aiStudioService'
import { createDefaultModule } from '../data/moduleSchema'
import { modules as builtInModules, SEED_COURSES } from '../data/modules'
import { useConfig } from '../context/ConfigContext'

const PROMPT_SUGGESTIONS = [
  'Build a GitHub 101 module explaining Repos, Branches, Local vs Cloud, and the Push/Pull workflow',
  'Build a 3-page module on XLOOKUP vs VLOOKUP with practical commercial examples',
  'Create a training lesson from this Google Doc: https://docs.google.com/document/d/...',
  'Teach new hires how to troubleshoot #N/A, #VALUE! and broken BigQuery formula imports',
  'Build an Apps Script tutorial showing how to automate daily Slack email notifications',
]

const QUICK_REFINEMENTS = [
  'Add 2 harder quiz questions with explanations',
  'Add a warning callout about timezone and date formatting',
  'Use real Australian partner names (CommBank, NAB, Youi)',
  'Add an edge-case troubleshooting page',
]

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

export default function ModuleStudioPage() {
  const { draftId } = useParams()
  const { user, isManager, isAdmin } = useAuth()
  const navigate = useNavigate()

  const [activeDraftId, setActiveDraftId] = useState(draftId || '')
  const { courses, refreshCustomModules } = useConfig()
  const [currentSchema, setCurrentSchema] = useState(null)
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content:
        "👋 Welcome to the AI Training Studio! What would you like to build today?\n\nYou can describe any topic in plain English, paste a screenshot directly (Ctrl+V), upload an SOP doc/image, or paste links to Google Docs or Confluence SOPs.",
    },
  ])
  const [inputText, setInputText] = useState('')
  const [sourceUrl, setSourceUrl] = useState('')
  const [showUrlInput, setShowUrlInput] = useState(false)
  const [attachedFile, setAttachedFile] = useState(null)
  const [isDraggingFile, setIsDraggingFile] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [lastSavedAt, setLastSavedAt] = useState(null)
  const [isPublishing, setIsPublishing] = useState(false)
  const [publishSuccess, setPublishSuccess] = useState(false)
  const [viewMode, setViewMode] = useState('desktop') // 'desktop' | 'mobile'
  const [versionHistory, setVersionHistory] = useState([])
  const [isEditorModalOpen, setIsEditorModalOpen] = useState(false)

  const [draftsList, setDraftsList] = useState([])
  const [showDraftsModal, setShowDraftsModal] = useState(false)
  const [isLoadingDrafts, setIsLoadingDrafts] = useState(false)
  const [allLiveModules, setAllLiveModules] = useState([])
  const [existingModulesSearch, setExistingModulesSearch] = useState('')
  const [selectedCourseFilter, setSelectedCourseFilter] = useState('all')

  const chatEndRef = useRef(null)
  const fileInputRef = useRef(null)

  const LS_BACKUP_KEY = 'cs_studio_draft_backup'

  const loadUserDrafts = async () => {
    setIsLoadingDrafts(true)
    try {
      const list = await listDrafts(user)
      setDraftsList(list)
    } catch (e) {
      console.error('Failed to load drafts list:', e)
    } finally {
      setIsLoadingDrafts(false)
    }
  }

  const loadAllExistingModules = async () => {
    try {
      const customMods = await loadPublishedCustomModules()
      const merged = [
        ...builtInModules.map((m) => ({ ...m, isBuiltin: true })),
        ...customMods.filter((c) => !builtInModules.some((b) => String(b.id) === String(c.id))),
      ]
      setAllLiveModules(merged)
    } catch (e) {
      console.error('Failed to load live modules:', e)
      setAllLiveModules(builtInModules.map((m) => ({ ...m, isBuiltin: true })))
    }
  }

  useEffect(() => {
    loadUserDrafts()
    loadAllExistingModules()
  }, [user])

  // Scroll chat to bottom on new messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isGenerating, attachedFile])

  // Load existing draft if draftId is in URL
  useEffect(() => {
    if (draftId) {
      setActiveDraftId(draftId)
      loadModuleForStudio(draftId).then((data) => {
        if (data?.currentSchema) {
          setCurrentSchema(data.currentSchema)
          if (data.messages?.length) {
            setMessages(data.messages)
          } else {
            const label = data.sourceType === 'builtin' ? 'Core Built-in Module' : 'Published Module'
            setMessages([
              {
                role: 'assistant',
                content: `👋 Loaded **${data.currentSchema.title}** (${label}).\n\nWhat changes or updates would you like to make?\n\nYou can ask me to:\n- Revise or expand explanations and concepts\n- Add interactive spreadsheet practice tables\n- Add or update quiz questions and multiple-choice options\n- Tailor content with internal team SOPs or real-world examples\n\nWhen ready, click **Publish to Catalog** to save and push your updates live!`,
              },
            ])
          }
        }
      })
    } else {
      setCurrentSchema(null)
      setActiveDraftId('')
      loadUserDrafts()
      loadAllExistingModules()
    }
  }, [draftId])

  const handleSelectDraft = (d) => {
    if (d?.currentSchema) {
      const canonicalId = String(d.currentSchema.id || d.draftId)
      setCurrentSchema(d.currentSchema)
      if (d.messages?.length) setMessages(d.messages)
      setActiveDraftId(canonicalId)
      navigate(`/admin/studio/${canonicalId}`, { replace: true })
      setShowDraftsModal(false)
    }
  }

  const handleDeleteDraftFromList = async (e, dId, schemaId) => {
    e.stopPropagation()
    if (!window.confirm('Are you sure you want to delete this draft?')) return
    await deleteDraft(dId)
    if (schemaId && schemaId !== dId) {
      await deleteDraft(schemaId)
    }
    setDraftsList((prev) =>
      prev.filter(
        (d) =>
          d.draftId !== dId &&
          d.draftId !== schemaId &&
          d.currentSchema?.id !== dId &&
          d.currentSchema?.id !== schemaId
      )
    )
    if (activeDraftId === dId || activeDraftId === schemaId) {
      setCurrentSchema(null)
      setActiveDraftId('')
      try {
        localStorage.removeItem(LS_BACKUP_KEY)
      } catch (err) {}
      navigate('/admin/studio', { replace: true })
    }
  }

  const handleStartBlankModule = () => {
    const defaultSchema = createDefaultModule({
      id: `custom-mod-${Date.now().toString(36)}`,
      title: 'New Custom Training Module',
      subtitle: 'Created with AI Studio',
      // Never 'General': that is not a course id anything declares, so it
      // would file the module under a course invented by the fallback itself.
      track: courses[0]?.id || SEED_COURSES[0]?.id,
      phase: 'Phase 1',
    })
    setCurrentSchema(defaultSchema)
    setActiveDraftId(defaultSchema.id)
    setVersionHistory([])
    setMessages([
      {
        role: 'assistant',
        content:
          "👋 Created a new blank module template for you! You can use the chat on the left to add content, quizzes, and spreadsheet exercises, or click **'Edit Content & Quiz'** above to customize directly.",
      },
    ])
    navigate(`/admin/studio/${defaultSchema.id}`, { replace: true })
    setShowDraftsModal(false)
  }

  // Auto-save draft in background on schema changes when editing a draft
  useEffect(() => {
    if (draftId && currentSchema) {
      const canonicalId = String(currentSchema.id || activeDraftId || draftId)
      if (activeDraftId !== canonicalId) {
        setActiveDraftId(canonicalId)
      }

      try {
        localStorage.setItem(
          LS_BACKUP_KEY,
          JSON.stringify({ currentSchema, messages, activeDraftId: canonicalId, updatedAt: new Date().toISOString() })
        )
      } catch (err) {
        // LocalStorage quota or private mode
      }

      if (user) {
        saveDraft(canonicalId, { currentSchema, messages }, user).then((res) => {
          if (res?.success) {
            setLastSavedAt(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }))
          }
        })
      }
    }
  }, [currentSchema, activeDraftId, messages, user, draftId])

  const handleProcessFile = (file) => {
    if (!file) return
    const isImage = file.type.startsWith('image/')
    const isPdf = file.type === 'application/pdf'

    if (isImage || isPdf) {
      const reader = new FileReader()
      reader.onload = (loadEvent) => {
        setAttachedFile({
          name: file.name || (isImage ? 'Pasted_Screenshot.png' : 'Document.pdf'),
          type: file.type || (isImage ? 'image/png' : 'application/pdf'),
          isImage,
          dataUrl: loadEvent.target.result,
          sizeStr: formatBytes(file.size),
        })
      }
      reader.readAsDataURL(file)
    } else {
      // Plain text, Markdown, CSV, JSON, code
      const reader = new FileReader()
      reader.onload = (loadEvent) => {
        setAttachedFile({
          name: file.name || 'document.txt',
          type: file.type || 'text/plain',
          isImage: false,
          text: loadEvent.target.result,
          sizeStr: formatBytes(file.size),
        })
      }
      reader.readAsText(file)
    }
  }

  const handlePaste = (e) => {
    const items = e.clipboardData?.items
    if (!items) return

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile()
        if (file) {
          e.preventDefault()
          const reader = new FileReader()
          reader.onload = (loadEvent) => {
            setAttachedFile({
              name: `Pasted Screenshot ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}.png`,
              type: file.type || 'image/png',
              isImage: true,
              dataUrl: loadEvent.target.result,
              sizeStr: formatBytes(file.size),
            })
          }
          reader.readAsDataURL(file)
          break
        }
      }
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDraggingFile(false)
    const file = e.dataTransfer?.files?.[0]
    if (file) {
      handleProcessFile(file)
    }
  }

  const handleSaveDraft = async () => {
    if (!currentSchema) return
    setIsSaving(true)
    setSaveError(null)
    try {
      const canonicalId = String(currentSchema.id || activeDraftId)
      const res = await saveDraft(canonicalId, { currentSchema, messages }, user)
      if (res?.success) {
        setSaveSuccess(true)
        setLastSavedAt(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }))
        loadUserDrafts()
        setTimeout(() => setSaveSuccess(false), 3000)
      } else {
        setSaveError(res?.error || 'Failed to save draft')
        setTimeout(() => setSaveError(null), 4000)
      }
    } catch (err) {
      setSaveError(err.message || 'Error saving draft')
      setTimeout(() => setSaveError(null), 4000)
    } finally {
      setIsSaving(false)
    }
  }

  const handleApplyManualEdits = (updatedSchema) => {
    if (!updatedSchema) return
    setCurrentSchema(updatedSchema)
    const canonicalId = String(updatedSchema.id || activeDraftId)
    saveDraft(canonicalId, { currentSchema: updatedSchema, messages }, user).then((res) => {
      if (res?.success) {
        setLastSavedAt(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }))
        loadUserDrafts()
      }
    })
  }

  /**
   * Change the course a module is filed under.
   *
   * Until now `track` was set once — by the generator or by the blank-module
   * default — DISPLAYED as "Track: <id>", and never editable. That is how a
   * module came to name a course that did not exist: there was no course list
   * to pick from, no way to correct it, and the label used a word the rest of
   * the app had retired. Routed through the same commit path as a manual edit
   * so the draft is saved exactly as any other change is.
   */
  // A course id the course list does not declare. Tested against `courses`,
  // the real list — NOT `allCourses`, which folds in the course fabricated FROM
  // this very field and would therefore always report it as known.
  const isUndeclaredCourse = Boolean(
    currentSchema?.track && !courses.some((c) => c.id === currentSchema.track),
  )

  const handleCourseChange = (courseId) => {
    if (!currentSchema || !courseId || courseId === currentSchema.track) return
    handleApplyManualEdits({ ...currentSchema, track: courseId })
  }

  const handleSendPrompt = async (promptToSend) => {
    const text = promptToSend || inputText
    if ((!text.trim() && !attachedFile) || isGenerating) return

    const fileToSend = attachedFile
    const userMessage = {
      role: 'user',
      content: text.trim() || (fileToSend?.isImage ? '📸 [Pasted Screenshot]' : `📎 [Attached: ${fileToSend?.name}]`),
      ...(fileToSend?.isImage ? { image: fileToSend.dataUrl } : {}),
      ...(fileToSend && !fileToSend.isImage ? { fileName: fileToSend.name, fileSize: fileToSend.sizeStr } : {}),
    }
    const updatedMessages = [...messages, userMessage]
    setMessages(updatedMessages)
    setInputText('')
    setAttachedFile(null)
    setIsGenerating(true)

    try {
      if (currentSchema) {
        setVersionHistory((prev) => [...prev, { schema: currentSchema, time: new Date().toLocaleTimeString() }])
      }

      const result = await generateOrRefineModule({
        prompt: text,
        currentSchema,
        conversationHistory: updatedMessages,
        sourceUrl,
        image: fileToSend?.isImage ? fileToSend.dataUrl : null,
        file: fileToSend && !fileToSend.isImage ? { name: fileToSend.name, text: fileToSend.text, data: fileToSend.dataUrl, type: fileToSend.type } : null,
      })

      setCurrentSchema(result.schema)
      setMessages((prev) => [...prev, { role: 'assistant', content: result.assistantMessage }])
      if (sourceUrl) setSourceUrl('')
      setShowUrlInput(false)
      
      const canonicalId = String(result.schema?.id || activeDraftId || `draft-${Date.now().toString(36)}`)
      setActiveDraftId(canonicalId)
      if (draftId !== canonicalId) {
        navigate(`/admin/studio/${canonicalId}`, { replace: true })
      }
      loadUserDrafts()
    } catch (err) {
      console.error('Generation error:', err)
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Sorry, I encountered an issue generating that section. Please try refining your request.',
        },
      ])
    } finally {
      setIsGenerating(false)
    }
  }

  const handleUndo = () => {
    if (versionHistory.length === 0) return
    const prev = versionHistory[versionHistory.length - 1]
    setCurrentSchema(prev.schema)
    setVersionHistory((v) => v.slice(0, -1))
    setMessages((prevMsg) => [
      ...prevMsg,
      { role: 'assistant', content: 'Reverted to the previous version.' },
    ])
  }

  const handlePublish = async () => {
    if (!currentSchema) return
    setIsPublishing(true)
    const res = await publishCustomModule(currentSchema.id, currentSchema, user)
    setIsPublishing(false)
    if (res.success) {
      setPublishSuccess(true)
      // The shared custom-module list is loaded ONCE per session, so publishing
      // without refreshing it leaves every other page rendering the module as
      // it was BEFORE the publish — its old title, its old course, and its old
      // URL, because a link is built from that cached object. The write landed;
      // the app just kept showing the previous version until a reload.
      //
      // Most visible with slugs: a module published for the first time mints
      // one, and the course page went on linking to it by id.
      await refreshCustomModules()
      const idsToDelete = new Set([activeDraftId, draftId, currentSchema?.id].filter(Boolean))
      for (const id of idsToDelete) {
        await deleteDraft(id)
      }
      loadUserDrafts()
      try {
        localStorage.removeItem(LS_BACKUP_KEY)
      } catch (e) {}
      setTimeout(() => setPublishSuccess(false), 5000)
    }
  }

  const handleDiscardDraft = async () => {
    if (!window.confirm('Discard current draft and return to Studio Hub?')) return
    const idsToDelete = new Set([activeDraftId, draftId, currentSchema?.id].filter(Boolean))
    for (const id of idsToDelete) {
      await deleteDraft(id)
    }
    try {
      localStorage.removeItem(LS_BACKUP_KEY)
    } catch (e) {}
    await loadUserDrafts()
    setCurrentSchema(null)
    setActiveDraftId('')
    setVersionHistory([])
    setMessages([
      {
        role: 'assistant',
        content:
          "👋 Welcome to the AI Training Studio! What would you like to build today?\n\nYou can describe any topic in plain English or paste links to your Google Docs or Confluence SOPs.",
      },
    ])
    navigate('/admin/studio', { replace: true })
  }

  // --------------------------------------------------------------------------
  // RENDER: STUDIO HUB VIEW (when not actively editing a module / draft)
  // --------------------------------------------------------------------------
  const isHubView = !draftId

  if (isHubView) {
    const filteredExisting = allLiveModules.filter((m) => {
      const matchSearch =
        !existingModulesSearch ||
        m.title?.toLowerCase().includes(existingModulesSearch.toLowerCase()) ||
        m.id?.toString().includes(existingModulesSearch.toLowerCase())
      const matchCourse = selectedCourseFilter === 'all' || m.track === selectedCourseFilter
      return matchSearch && matchCourse
    })

    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        {/* Hub Header */}
        <header className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-30 shadow-2xs">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link
                to="/admin/modules"
                className="p-2 rounded-xl text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors inline-flex items-center gap-1.5 text-xs font-semibold"
                title="Back to Admin Modules"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Training Modules</span>
              </Link>
              <span className="text-slate-300">/</span>
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-xl bg-gradient-to-tr from-indigo-500 to-purple-600 text-white shadow-xs">
                  <Sparkles className="h-4 w-4" />
                </div>
                <h1 className="text-sm font-bold text-slate-900">AI Module Studio</h1>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Link
                to="/admin/modules"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 text-xs font-semibold shadow-2xs transition-colors"
              >
                <FolderKanban className="h-3.5 w-3.5 text-slate-500" />
                <span>Manage Modules</span>
              </Link>
              <Link
                to="/admin/docs"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 text-xs font-semibold shadow-2xs transition-colors"
              >
                <BookOpen className="h-3.5 w-3.5 text-slate-500" />
                <span>Guides & SOPs</span>
              </Link>
            </div>
          </div>
        </header>

        {/* Hub Hero & Action Center */}
        <main className="max-w-7xl mx-auto w-full px-6 py-8 flex-1 flex flex-col gap-8">
          {/* Hero Banner */}
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-200/80 text-indigo-700 text-xs font-bold mb-3 shadow-2xs">
              <Sparkles className="h-3.5 w-3.5" />
              <span>Finder University AI Course Builder</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              What would you like to build today?
            </h2>
            <p className="text-sm text-slate-500 mt-2">
              Create interactive lessons with quizzes and spreadsheet exercises from plain text, or resume an existing draft.
            </p>
          </div>

          {/* Primary Two-Card Hub Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            {/* OPTION 1: Create New Module from Scratch (7 cols) */}
            <div className="lg:col-span-7 bg-white rounded-3xl border border-slate-200/90 shadow-sm p-6 flex flex-col">
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-2xl bg-indigo-50 text-indigo-600">
                    <Sparkles className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-900">Option A: Create New Module</h3>
                    <p className="text-xs text-slate-500">Describe what you want to teach or attach an SOP</p>
                  </div>
                </div>
                <button
                  onClick={handleStartBlankModule}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-xs font-semibold text-slate-700 shadow-2xs transition-colors"
                  title="Start with an empty editable template"
                >
                  <LayoutTemplate className="h-3.5 w-3.5 text-slate-500" />
                  <span>Start Blank</span>
                </button>
              </div>

              {/* Prompt Box */}
              <div
                onPaste={handlePaste}
                onDragOver={(e) => {
                  e.preventDefault()
                  setIsDraggingFile(true)
                }}
                onDragLeave={() => setIsDraggingFile(false)}
                onDrop={handleDrop}
                className={`border rounded-2xl p-3 bg-slate-50 transition-colors ${
                  isDraggingFile ? 'border-indigo-500 bg-indigo-50/50 ring-2 ring-indigo-400' : 'border-slate-200'
                }`}
              >
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleSendPrompt()
                    }
                  }}
                  placeholder="E.g., Build a 3-page GitHub 101 course covering Repos, Branches, Pull Requests, and a quiz..."
                  rows={4}
                  className="w-full bg-transparent text-sm text-slate-800 placeholder-slate-400 focus:outline-none resize-none"
                />

                {/* Attached File/Screenshot Preview */}
                {attachedFile && (
                  <div className="my-2 p-2 bg-indigo-50 border border-indigo-200 rounded-xl flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      {attachedFile.isImage ? (
                        <img
                          src={attachedFile.dataUrl}
                          alt="Preview"
                          className="h-8 w-8 rounded-lg object-cover border border-indigo-200 shrink-0"
                        />
                      ) : (
                        <FileText className="h-5 w-5 text-indigo-600 shrink-0" />
                      )}
                      <span className="text-xs font-semibold text-indigo-950 truncate max-w-[200px]">
                        {attachedFile.name}
                      </span>
                      <span className="text-[10px] text-indigo-500">({attachedFile.sizeStr})</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAttachedFile(null)}
                      className="text-indigo-400 hover:text-indigo-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}

                {/* Source Doc URL Input */}
                {showUrlInput && (
                  <div className="mt-2 p-2 bg-white border border-blue-200 rounded-xl flex items-center gap-2">
                    <Link2 className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                    <input
                      type="url"
                      value={sourceUrl}
                      onChange={(e) => setSourceUrl(e.target.value)}
                      placeholder="Paste Google Doc or Confluence SOP link..."
                      className="flex-1 text-xs text-slate-800 placeholder-slate-400 focus:outline-none"
                    />
                    <button
                      onClick={() => setShowUrlInput(false)}
                      className="text-slate-400 hover:text-slate-600 text-xs px-1"
                    >
                      ✕
                    </button>
                  </div>
                )}

                <div className="mt-3 pt-2.5 border-t border-slate-200/80 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className={`p-1.5 rounded-lg border text-xs font-medium inline-flex items-center gap-1 transition-colors ${
                        attachedFile
                          ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                          : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-100'
                      }`}
                      title="Upload file or screenshot (Ctrl+V supported)"
                    >
                      <Paperclip className="h-3.5 w-3.5" />
                      <span className="text-[11px] hidden sm:inline">Attach SOP / Image</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setShowUrlInput(!showUrlInput)}
                      className={`p-1.5 rounded-lg border text-xs font-medium inline-flex items-center gap-1 transition-colors ${
                        showUrlInput || sourceUrl
                          ? 'bg-blue-100 border-blue-300 text-blue-700'
                          : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-100'
                      }`}
                      title="Attach Google Doc or Confluence link"
                    >
                      <Link2 className="h-3.5 w-3.5" />
                      <span className="text-[11px] hidden sm:inline">SOP Link</span>
                    </button>
                  </div>

                  <button
                    onClick={() => handleSendPrompt()}
                    disabled={(!inputText.trim() && !attachedFile) || isGenerating}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white transition-all shadow-xs ${
                      (!inputText.trim() && !attachedFile) || isGenerating
                        ? 'bg-slate-300 cursor-not-allowed'
                        : 'bg-indigo-600 hover:bg-indigo-700 active:scale-95'
                    }`}
                  >
                    {isGenerating ? (
                      <>
                        <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-3.5 w-3.5" />
                        <span>Generate with AI</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Inspiration suggestions */}
              <div className="mt-4">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-2">
                  Quick Inspiration Ideas:
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {PROMPT_SUGGESTIONS.map((sug, i) => (
                    <button
                      key={i}
                      onClick={() => handleSendPrompt(sug)}
                      className="text-[11px] bg-slate-50 border border-slate-200 hover:border-indigo-300 hover:text-indigo-600 text-slate-600 px-2.5 py-1.5 rounded-xl transition-all shadow-2xs text-left"
                    >
                      {sug}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* OPTION 2: Resume In-Progress Drafts (5 cols) */}
            <div className="lg:col-span-5 bg-white rounded-3xl border border-slate-200/90 shadow-sm p-6 flex flex-col min-h-[420px]">
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-2xl bg-amber-50 text-amber-600">
                    <FolderOpen className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-bold text-slate-900">Option B: In-Progress Drafts</h3>
                      {draftsList.length > 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold">
                          {draftsList.length}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">Resume unfinished work or discard</p>
                  </div>
                </div>

                <button
                  onClick={loadUserDrafts}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                  title="Refresh drafts"
                >
                  <RefreshCw className={`h-4 w-4 ${isLoadingDrafts ? 'animate-spin' : ''}`} />
                </button>
              </div>

              {/* Drafts List Container */}
              <div className="flex-1 overflow-y-auto max-h-[380px] space-y-2.5 pr-1">
                {isLoadingDrafts ? (
                  <div className="py-12 text-center text-slate-400 flex flex-col items-center gap-2">
                    <RefreshCw className="h-6 w-6 animate-spin text-indigo-500" />
                    <span className="text-xs font-medium">Checking saved drafts...</span>
                  </div>
                ) : draftsList.length === 0 ? (
                  <div className="py-16 text-center text-slate-400 flex flex-col items-center justify-center h-full">
                    <BookOpen className="h-10 w-10 text-slate-300 mb-2" />
                    <p className="text-sm font-semibold text-slate-700">No active drafts</p>
                    <p className="text-xs text-slate-400 mt-1 max-w-xs">
                      When you generate or edit modules, your work-in-progress drafts will automatically appear here.
                    </p>
                  </div>
                ) : (
                  draftsList.map((d) => {
                    const schema = d.currentSchema || {}
                    const pageCount = schema.pages?.length || 0
                    const updatedStr = d.updatedAt
                      ? new Date(d.updatedAt).toLocaleString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : 'Recently'

                    return (
                      <div
                        key={d.draftId}
                        onClick={() => handleSelectDraft(d)}
                        className="p-3.5 rounded-2xl bg-slate-50/80 hover:bg-indigo-50/50 border border-slate-200/80 hover:border-indigo-200 transition-all cursor-pointer flex items-center justify-between gap-3 group"
                      >
                        <div className="min-w-0 flex-1">
                          <h4 className="text-xs font-bold text-slate-900 group-hover:text-indigo-600 transition-colors truncate">
                            {schema.title || 'Untitled Draft'}
                          </h4>
                          <div className="flex flex-wrap items-center gap-2 mt-1 text-[11px] text-slate-500">
                            <span className="font-medium text-slate-600">
                              {pageCount} page{pageCount !== 1 ? 's' : ''}
                            </span>
                            <span>•</span>
                            <span>{updatedStr}</span>
                            {schema.track && (
                              <>
                                <span>•</span>
                                <span className="px-1.5 py-0.2 rounded bg-slate-200/70 text-slate-700 text-[10px] font-semibold">
                                  {schema.track}
                                </span>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5 shrink-0">
                          <button
                            onClick={() => handleSelectDraft(d)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 transition-all shadow-2xs"
                          >
                            <span>Resume</span>
                            <ArrowRight className="h-3 w-3" />
                          </button>
                          <button
                            onClick={(e) => handleDeleteDraftFromList(e, d.draftId, d.currentSchema?.id)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            title="Delete draft"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            </div>
          </div>

          {/* OPTION 3: Edit an Existing / Published Module */}
          <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-2xl bg-emerald-50 text-emerald-600">
                  <Edit3 className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">Option C: Edit an Existing Course</h3>
                  <p className="text-xs text-slate-500">
                    Open any built-in or custom module directly in AI Studio to update explanations, practice sheets, or quizzes
                  </p>
                </div>
              </div>

              {/* Filters */}
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={existingModulesSearch}
                    onChange={(e) => setExistingModulesSearch(e.target.value)}
                    placeholder="Search existing modules..."
                    className="pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-1 focus:ring-indigo-500"
                  />
                </div>

                <select
                  value={selectedCourseFilter}
                  onChange={(e) => setSelectedCourseFilter(e.target.value)}
                  className="py-1.5 px-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl text-slate-700 font-medium focus:outline-none"
                >
                  <option value="all">All Tracks</option>
                  {SEED_COURSES.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Modules Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[340px] overflow-y-auto pr-1">
              {filteredExisting.slice(0, 30).map((mod) => (
                <div
                  key={mod.id}
                  onClick={() => navigate(`/admin/studio/${mod.id}`)}
                  className="p-3.5 rounded-2xl bg-slate-50 hover:bg-indigo-50/60 border border-slate-200/80 hover:border-indigo-200 transition-all cursor-pointer flex items-center justify-between gap-3 group"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="px-1.5 py-0.2 rounded bg-slate-200 text-slate-700 text-[9px] font-bold uppercase">
                        ID: {mod.id}
                      </span>
                      <span className="px-1.5 py-0.2 rounded bg-indigo-100 text-indigo-700 text-[9px] font-semibold">
                        {mod.track || 'General'}
                      </span>
                    </div>
                    <h4 className="text-xs font-bold text-slate-900 group-hover:text-indigo-600 transition-colors truncate">
                      {mod.title}
                    </h4>
                  </div>
                  <button
                    onClick={() => navigate(`/admin/studio/${mod.id}`)}
                    className="p-2 rounded-xl bg-white border border-slate-200 group-hover:border-indigo-300 text-slate-600 group-hover:text-indigo-600 shadow-2xs transition-all shrink-0"
                    title="Edit in AI Studio"
                  >
                    <Edit3 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </main>

        {/* Hidden File Picker Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf,.csv,.txt,.md,.json,.sql,.js,.py"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) handleProcessFile(file)
            e.target.value = ''
          }}
        />
      </div>
    )
  }

  // --------------------------------------------------------------------------
  // RENDER: STUDIO WORKSPACE VIEW (Split Screen Chat + Live Interactive Preview)
  // --------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Top Studio Workspace Bar */}
      <header className="bg-white border-b border-slate-200 px-6 py-3.5 flex items-center justify-between sticky top-0 z-30 shadow-2xs">
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setCurrentSchema(null)
              setActiveDraftId('')
              navigate('/admin/studio')
            }}
            className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors inline-flex items-center gap-1 text-xs font-semibold"
            title="Return to Studio Hub selection screen"
          >
            <ChevronRight className="h-4 w-4 rotate-180" />
            <span className="hidden sm:inline">Hub</span>
          </button>
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-xl bg-gradient-to-tr from-indigo-500 to-purple-600 text-white shadow-xs">
              <Sparkles className="h-4 w-4" />
            </div>
            <div>
              <h1 className="text-sm font-bold text-slate-900 leading-tight">
                {currentSchema?.title || 'AI Module Studio'}
              </h1>
              {currentSchema ? (
                <p className="text-[11px] text-slate-500 font-medium flex flex-wrap items-center gap-x-1.5 gap-y-1">
                  <span>ID: {currentSchema.id}</span>
                  <span aria-hidden="true">•</span>
                  <label htmlFor="studio-course" className="sr-only">Course this module belongs to</label>
                  <span>Course:</span>
                  <select
                    id="studio-course"
                    value={currentSchema.track || ''}
                    onChange={(e) => handleCourseChange(e.target.value)}
                    className="rounded border border-slate-200 bg-white px-1 py-0.5 text-[11px] font-semibold text-slate-700 hover:border-slate-300 focus:border-indigo-300 focus:outline-none"
                  >
                    {!currentSchema.track && <option value="">— pick a course —</option>}
                    {courses.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                    {isUndeclaredCourse && (
                      <option value={currentSchema.track}>{currentSchema.track} (new course)</option>
                    )}
                  </select>
                  {isUndeclaredCourse && (
                    <span className="inline-flex items-center gap-1 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 font-semibold text-amber-800">
                      <AlertTriangle className="h-3 w-3" />
                      no course declares “{currentSchema.track}” — publishing creates it
                    </span>
                  )}
                </p>
              ) : (
                <p className="text-[11px] text-slate-500 font-medium">Natural Language Module Builder</p>
              )}
            </div>
          </div>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-2.5">
          {/* Saved Drafts Switcher Button */}
          <button
            onClick={() => {
              loadUserDrafts()
              setShowDraftsModal(true)
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-xs font-semibold text-slate-700 shadow-2xs transition-colors"
            title="Browse and resume saved drafts"
          >
            <FolderOpen className="h-3.5 w-3.5 text-indigo-600" />
            <span>My Drafts</span>
            {draftsList.length > 0 && (
              <span className="px-1.5 py-0.2 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-bold">
                {draftsList.length}
              </span>
            )}
          </button>

          {(isManager || isAdmin) && (
            <Link
              to="/admin/modules"
              className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-indigo-200 bg-indigo-50 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 transition-colors"
              title="Manage and delete published modules"
            >
              <FolderKanban className="h-3.5 w-3.5" />
              Manage Modules
            </Link>
          )}

          {currentSchema && (
            <button
              onClick={handleDiscardDraft}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-red-200 bg-red-50/50 text-xs font-semibold text-red-600 hover:bg-red-100/80 transition-colors"
              title="Discard draft and return to Hub"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Discard
            </button>
          )}

          {versionHistory.length > 0 && (
            <button
              onClick={handleUndo}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
              title="Undo last change"
            >
              <Undo2 className="h-3.5 w-3.5" />
              Undo
            </button>
          )}

          {/* Device Preview Switcher */}
          <div className="hidden sm:flex items-center bg-slate-100 p-0.5 rounded-xl border border-slate-200">
            <button
              onClick={() => setViewMode('desktop')}
              className={`p-1.5 rounded-lg text-xs font-medium transition-all ${
                viewMode === 'desktop' ? 'bg-white text-indigo-600 shadow-2xs' : 'text-slate-600 hover:text-slate-900'
              }`}
              title="Desktop View"
            >
              <Laptop className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => setViewMode('mobile')}
              className={`p-1.5 rounded-lg text-xs font-medium transition-all ${
                viewMode === 'mobile' ? 'bg-white text-indigo-600 shadow-2xs' : 'text-slate-600 hover:text-slate-900'
              }`}
              title="Mobile View"
            >
              <Smartphone className="h-3.5 w-3.5" />
            </button>
          </div>

          {lastSavedAt && (
            <span className="hidden md:inline-flex items-center text-[11px] text-slate-400 font-medium">
              Saved {lastSavedAt}
            </span>
          )}

          <button
            onClick={() => setIsEditorModalOpen(true)}
            disabled={!currentSchema}
            className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl border text-xs font-semibold transition-all shadow-2xs ${
              !currentSchema
                ? 'bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed'
                : 'border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 active:scale-98'
            }`}
            title="Inspect and edit section content, questions, and correct answers directly"
          >
            <Edit3 className="h-3.5 w-3.5" />
            <span>Edit Content & Quiz</span>
          </button>

          <button
            onClick={handleSaveDraft}
            disabled={!currentSchema || isSaving}
            className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl border text-xs font-semibold transition-all shadow-2xs ${
              saveSuccess
                ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                : saveError
                ? 'bg-red-50 border-red-300 text-red-700'
                : !currentSchema
                ? 'bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 active:scale-98'
            }`}
            title="Save draft to cloud"
          >
            {saveSuccess ? (
              <>
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                <span>Draft Saved!</span>
              </>
            ) : isSaving ? (
              <>
                <RefreshCw className="h-3.5 w-3.5 animate-spin text-slate-500" />
                <span>Saving...</span>
              </>
            ) : saveError ? (
              <>
                <AlertCircle className="h-3.5 w-3.5 text-red-600" />
                <span>Save Failed</span>
              </>
            ) : (
              <>
                <Save className="h-3.5 w-3.5 text-slate-500" />
                <span>Save Draft</span>
              </>
            )}
          </button>

          <button
            onClick={handlePublish}
            disabled={!currentSchema || isPublishing}
            className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-xs font-semibold text-white transition-all shadow-xs ${
              publishSuccess
                ? 'bg-emerald-600'
                : !currentSchema
                ? 'bg-slate-300 cursor-not-allowed'
                : 'bg-indigo-600 hover:bg-indigo-700 active:scale-98'
            }`}
          >
            {publishSuccess ? (
              <>
                <CheckCircle2 className="h-3.5 w-3.5" />
                Published to Catalog!
              </>
            ) : isPublishing ? (
              <>
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                Publishing...
              </>
            ) : (
              <>
                <Sparkles className="h-3.5 w-3.5" />
                Publish to Catalog
              </>
            )}
          </button>
        </div>
      </header>

      {/* Main Studio Split Screen */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 min-h-[calc(100vh-57px)]">
        {/* LEFT COLUMN: AI Chat & Prompt Controls (5 cols) */}
        <div className="lg:col-span-5 bg-white border-r border-slate-200 flex flex-col h-full max-h-[calc(100vh-57px)] sticky top-[57px]">
          {/* Chat Messages */}
          <div
            onPaste={handlePaste}
            onDragOver={(e) => {
              e.preventDefault()
              setIsDraggingFile(true)
            }}
            onDragLeave={() => setIsDraggingFile(false)}
            onDrop={handleDrop}
            className={`flex-1 overflow-y-auto p-4 space-y-4 transition-colors ${
              isDraggingFile ? 'bg-indigo-50/50 ring-2 ring-indigo-400 ring-inset' : ''
            }`}
          >
            {messages.map((msg, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'assistant' && (
                  <div className="h-7 w-7 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center shrink-0 mt-1">
                    <Bot className="h-4 w-4" />
                  </div>
                )}
                <div
                  className={`max-w-[85%] rounded-2xl p-3.5 text-xs leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-indigo-600 text-white font-medium shadow-xs'
                      : 'bg-slate-100 text-slate-800 border border-slate-200/80 whitespace-pre-wrap'
                  }`}
                >
                  {/* Render attached image in user bubble */}
                  {msg.image && (
                    <div className="mb-2.5">
                      <img
                        src={msg.image}
                        alt="Attached Screenshot"
                        className="max-h-52 w-auto max-w-full rounded-xl object-contain border border-white/20 bg-black/20 shadow-xs cursor-pointer hover:opacity-95 transition-opacity"
                        onClick={() => {
                          const w = window.open('')
                          if (w) {
                            w.document.write(`<html><head><title>Screenshot Preview</title></head><body style="margin:0;background:#0f172a;display:flex;align-items:center;justify-content:center;min-height:100vh;"><img src="${msg.image}" style="max-width:95vw;max-height:95vh;border-radius:12px;box-shadow:0 25px 50px -12px rgba(0,0,0,0.5);" /></body></html>`)
                          }
                        }}
                        title="Click to view full image"
                      />
                    </div>
                  )}

                  {/* Render non-image file badge in user bubble */}
                  {msg.fileName && !msg.image && (
                    <div className="mb-2 inline-flex items-center gap-2 px-2.5 py-1 rounded-lg bg-indigo-700/80 border border-indigo-400/40 text-white text-[11px] font-mono">
                      <FileText className="h-3.5 w-3.5 text-indigo-200" />
                      <span className="truncate max-w-[200px]">{msg.fileName}</span>
                      {msg.fileSize && <span className="opacity-80 text-[10px]">({msg.fileSize})</span>}
                    </div>
                  )}

                  <div>{msg.content}</div>
                </div>
                {msg.role === 'user' && (
                  <div className="h-7 w-7 rounded-xl bg-slate-800 text-white flex items-center justify-center shrink-0 mt-1">
                    <User className="h-4 w-4" />
                  </div>
                )}
              </motion.div>
            ))}

            {isGenerating && (
              <motion.div
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2.5 text-xs text-indigo-600 bg-indigo-50 border border-indigo-100 p-3 rounded-2xl w-fit"
              >
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                <span>AI assistant is analyzing your input & structuring your lesson...</span>
              </motion.div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Quick Suggestions Banner (if no schema yet or post-generation) */}
          <div className="p-3 border-t border-slate-100 bg-slate-50/70">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1.5">
              {!currentSchema ? 'Inspiration Prompts' : 'Refine with AI Assistant'}
            </span>
            <div className="flex flex-wrap gap-1.5">
              {(!currentSchema ? PROMPT_SUGGESTIONS : QUICK_REFINEMENTS).map((sug, i) => (
                <button
                  key={i}
                  onClick={() => handleSendPrompt(sug)}
                  className="text-[11px] bg-white border border-slate-200 hover:border-indigo-300 hover:text-indigo-600 text-slate-600 px-2.5 py-1 rounded-xl transition-all shadow-2xs truncate max-w-full text-left"
                >
                  {sug}
                </button>
              ))}
            </div>
          </div>

          {/* Optional Source Doc Link Input */}
          {showUrlInput && (
            <div className="px-4 py-2 border-t border-slate-100 bg-blue-50/50 flex items-center gap-2">
              <Link2 className="h-3.5 w-3.5 text-blue-600 shrink-0" />
              <input
                type="url"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                placeholder="Paste Google Doc or Confluence SOP URL..."
                className="flex-1 bg-white border border-blue-200 rounded-lg px-2.5 py-1 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={() => setShowUrlInput(false)}
                className="text-slate-400 hover:text-slate-600 text-xs px-1"
              >
                ✕
              </button>
            </div>
          )}

          {/* Hidden File Picker Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf,.csv,.txt,.md,.json,.sql,.js,.py"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) handleProcessFile(file)
              e.target.value = ''
            }}
          />

          {/* Attached File/Screenshot Preview Pill */}
          {attachedFile && (
            <div className="mx-3.5 mt-2 p-2 bg-indigo-50 border border-indigo-200/80 rounded-xl flex items-center justify-between gap-3 animate-in fade-in slide-in-from-bottom-1">
              <div className="flex items-center gap-2.5 min-w-0">
                {attachedFile.isImage ? (
                  <img
                    src={attachedFile.dataUrl}
                    alt="Preview"
                    className="h-10 w-10 rounded-lg object-cover border border-indigo-200 shrink-0 bg-white shadow-2xs"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center shrink-0">
                    <FileText className="h-5 w-5" />
                  </div>
                )}
                <div className="min-w-0 text-left">
                  <span className="block text-xs font-semibold text-indigo-950 truncate max-w-[220px]">
                    {attachedFile.name}
                  </span>
                  <span className="block text-[10px] text-indigo-600 font-medium">
                    {attachedFile.sizeStr} • Attached for AI multimodal analysis
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setAttachedFile(null)}
                className="p-1 rounded-lg text-indigo-400 hover:text-indigo-700 hover:bg-indigo-100 transition-colors"
                title="Remove attachment"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Chat Input Box */}
          <div
            className="p-3.5 border-t border-slate-200 bg-white"
            onDragOver={(e) => {
              e.preventDefault()
              setIsDraggingFile(true)
            }}
            onDragLeave={() => setIsDraggingFile(false)}
            onDrop={handleDrop}
          >
            <div className="flex items-center gap-2">
              {/* Upload Screenshot / Document Button */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className={`p-2 rounded-xl border transition-colors ${
                  attachedFile
                    ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                    : 'border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                }`}
                title="Upload screenshot or document (or paste via Ctrl+V)"
              >
                <Paperclip className="h-4 w-4" />
              </button>

              <button
                type="button"
                onClick={() => setShowUrlInput(!showUrlInput)}
                className={`p-2 rounded-xl border transition-colors ${
                  showUrlInput || sourceUrl
                    ? 'bg-blue-50 border-blue-300 text-blue-700'
                    : 'border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                }`}
                title="Attach Google Doc or Confluence SOP link"
              >
                <Link2 className="h-4 w-4" />
              </button>
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onPaste={handlePaste}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleSendPrompt()
                  }
                }}
                placeholder={
                  attachedFile
                    ? 'Add instructions for this attachment or press Enter...'
                    : !currentSchema
                    ? 'What would you like to build? (Paste screenshot Ctrl+V or type)...'
                    : 'Tell AI assistant how to refine (Paste screenshot Ctrl+V or type)...'
                }
                rows={2}
                className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 resize-none"
              />
              <button
                onClick={() => handleSendPrompt()}
                disabled={(!inputText.trim() && !attachedFile) || isGenerating}
                className={`p-2.5 rounded-xl text-white transition-all shadow-xs shrink-0 ${
                  (!inputText.trim() && !attachedFile) || isGenerating
                    ? 'bg-slate-300 cursor-not-allowed'
                    : 'bg-indigo-600 hover:bg-indigo-700 active:scale-95'
                }`}
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: Live Interactive Preview Canvas (7 cols) */}
        <div className="lg:col-span-7 bg-slate-100 p-6 flex flex-col items-center justify-start overflow-y-auto">
          {/* Live Preview Header Pill */}
          <div className="mb-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200 text-slate-600 text-xs font-semibold shadow-2xs">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            <span>Live Interactive Preview</span>
            <span className="text-slate-400">•</span>
            <span className="text-[11px] text-slate-500">Test quizzes & formulas directly</span>
          </div>

          {/* The Live Render Canvas Container */}
          <div
            className={`w-full transition-all duration-300 ${
              viewMode === 'mobile' ? 'max-w-md' : 'max-w-3xl'
            }`}
          >
            {currentSchema ? (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
                <div className="mb-6 pb-4 border-b border-slate-100 flex items-start justify-between gap-4">
                  <div>
                    <span className="inline-block px-2.5 py-0.5 rounded-md bg-indigo-50 text-indigo-700 text-[11px] font-bold uppercase tracking-wider mb-2">
                      {currentSchema.phase || 'Course'}
                    </span>
                    <h2 className="text-xl font-bold text-slate-900">{currentSchema.title}</h2>
                    {currentSchema.subtitle && (
                      <p className="text-sm text-slate-600 mt-1">{currentSchema.subtitle}</p>
                    )}
                  </div>
                </div>

                {/* Render the real dynamic module */}
                <DynamicModule moduleData={currentSchema} previewMode={true} />
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center flex flex-col items-center justify-center">
                <div className="h-12 w-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center mb-4">
                  <BookOpen className="h-6 w-6" />
                </div>
                <h3 className="text-base font-bold text-slate-800">Your live preview will appear here</h3>
                <p className="text-xs text-slate-500 max-w-sm mt-1 mb-6">
                  Type a prompt on the left or click one of the inspiration chips to generate an interactive training module in seconds.
                </p>
                <button
                  onClick={() => handleSendPrompt(PROMPT_SUGGESTIONS[0])}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-xl text-xs font-semibold transition-colors"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  Try Sample Module: "XLOOKUP vs VLOOKUP"
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Saved Drafts Modal */}
      <AnimatePresence>
        {showDraftsModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl border border-slate-200 shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
            >
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-xl bg-indigo-50 text-indigo-600">
                    <FolderOpen className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-slate-900">Saved Module Drafts</h2>
                    <p className="text-xs text-slate-500">Pick an in-progress draft to resume building</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleStartNewModule}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-xs font-semibold transition-colors"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    New Blank Module
                  </button>
                  <button
                    onClick={() => setShowDraftsModal(false)}
                    className="p-1.5 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 overflow-y-auto flex-1 divide-y divide-slate-100">
                {isLoadingDrafts ? (
                  <div className="py-12 text-center text-slate-400 flex flex-col items-center gap-2">
                    <RefreshCw className="h-6 w-6 animate-spin text-indigo-500" />
                    <span className="text-xs font-medium">Loading saved drafts...</span>
                  </div>
                ) : draftsList.length === 0 ? (
                  <div className="py-12 text-center text-slate-400 flex flex-col items-center">
                    <BookOpen className="h-10 w-10 text-slate-300 mb-2" />
                    <p className="text-sm font-semibold text-slate-700">No saved drafts yet</p>
                    <p className="text-xs text-slate-400 mt-0.5 max-w-xs">
                      When you generate or edit modules in the Studio, they are automatically saved here.
                    </p>
                  </div>
                ) : (
                  draftsList.map((d) => {
                    const schema = d.currentSchema || {}
                    const isCurrent = activeDraftId === d.draftId
                    const pageCount = schema.pages?.length || 0
                    const updatedStr = d.updatedAt
                      ? new Date(d.updatedAt).toLocaleString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : 'Recently'

                    return (
                      <div
                        key={d.draftId}
                        onClick={() => handleSelectDraft(d)}
                        className={`py-4 px-4 rounded-2xl flex items-center justify-between gap-4 transition-all cursor-pointer group ${
                          isCurrent
                            ? 'bg-indigo-50/70 border border-indigo-200'
                            : 'hover:bg-slate-50 border border-transparent'
                        }`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-bold text-slate-900 truncate group-hover:text-indigo-600 transition-colors">
                              {schema.title || 'Untitled Module Draft'}
                            </h3>
                            {isCurrent && (
                              <span className="px-2 py-0.5 rounded-full bg-indigo-600 text-white text-[10px] font-bold">
                                Current
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs text-slate-500">
                            <span className="inline-flex items-center gap-1 font-medium text-slate-600">
                              <Layers className="h-3 w-3 text-slate-400" />
                              {pageCount} page{pageCount !== 1 ? 's' : ''}
                            </span>
                            <span>•</span>
                            <span className="inline-flex items-center gap-1">
                              <Clock className="h-3 w-3 text-slate-400" />
                              {updatedStr}
                            </span>
                            {schema.track && (
                              <>
                                <span>•</span>
                                <span className="px-1.5 py-0.2 rounded bg-slate-100 text-slate-600 text-[10px] font-semibold">
                                  {schema.track}
                                </span>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={() => handleSelectDraft(d)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 transition-all shadow-2xs"
                          >
                            <span>Resume</span>
                            <ArrowRight className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={(e) => handleDeleteDraftFromList(e, d.draftId, d.currentSchema?.id)}
                            className="p-1.5 rounded-xl text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            title="Delete draft"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <ModuleContentEditorModal
        isOpen={isEditorModalOpen}
        onClose={() => setIsEditorModalOpen(false)}
        schema={currentSchema}
        onSave={handleApplyManualEdits}
      />
    </div>
  )
}
